<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { useUiStore } from '@/stores/ui'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import BaixaTituloFinanceiro from '@/components/Financeiro/movimentacoesBancarias/BaixaTituloFinanceiro.vue'
import SolicitacaoAdiantamentoModal from '@/components/Financeiro/contasPagarReceber/solicitacaoAdiantamento/SolicitacaoAdiantamentoModal.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { criarConfigBuscaCategoriaFinanceira } from '@/search-definitions/categoriaFinanceiraSearch'
import { FIN_ST_PLAN_CATEGFIN } from '@/constants/FIN_ST_PLAN_CATEGFIN'
import { dataHojeBR, formatarDataBR } from '@/utils/useDataBR'
import { ST } from '@/constants/ST'
import { FIN_TPTITULO } from '@/constants/FIN_TPTITULO'
import { FIN_NATUREZA_TITULO } from '@/constants/FIN_NATUREZA_TITULO'
import { ST_TITULO_FIN } from '@/constants/ST_TITULO_FIN'
import { MOE_FONTE_COTACAO } from '@/constants/MOE_FONTE_COTACAO'
import { FIN_TP_TAX_CONVERT } from '@/constants/FIN_TP_TAX_CONVERT'
import { FIN_ST_TAX_CONVERCAO } from '@/constants/FIN_ST_TAX_CONVERCAO'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idTituloFinanceiro: 0,
      lstTipoTitulo: '',
      lstStatusTitulo: '',
      lstNaturezaTitulo: FIN_NATUREZA_TITULO.LancamentosManuais,
      isAdiantamento: false,
      idTituloAdiantamentoVinculado: null,
      idSolicitacaoAdiantamento: null,
      idParceiro: null,
      nomeParceiro: '',
      idEmbarque: null,
      idDocumentoEntrada: null,
      idCondicaoPagamento: null,
      idPurchaseOrderCondicaoPg: null,
      idPedidoVendaCondicaoPg: null,
      numeroFaturaOriginal: '',
      observacaoLivre: '',
      numeroParcela: 1,
      totalParcelas: 1,
      dataEmissao: dataHojeBR(),
      dataVencimento: dataHojeBR(),
      valorTotal: null,
      saldoDevedor: null,
      lstMoeda: '',
      lstMoedaPadrao: '',
      taxaCambioPadrao: null,
      valorTotalMoedaPadrao: null,
      lstTaxaCambioStatus: '',
      dataReferenciaCambio: null,
      dataAlvoBuscaCambio: null,
      lstTipoTaxaConversao: FIN_TP_TAX_CONVERT.PTAXEmissaoTituloFinanceiro,
      lstMoedaGerencial: '',
      taxaCambioGerencial: null,
      taxaCambioGerencialPadrao: null,
      valorTotalMoedaGerencial: null,
      lstFonteCambio: MOE_FONTE_COTACAO.OlindaBCB,
      lstFormaPagamento: '',
      lstFormaPagamentoInternacional: '',
      codigoBarrasOuPix: '',
      idOrganizacao: null,
      itens: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const uiStore = useUiStore()

const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

// --- VISUALIZAÇÃO DA SOLICITAÇÃO DE ADIANTAMENTO VIA MODAL ---
const showModalSolicitacao = ref(false)
const isCarregandoSolicitacao = ref(false)
const solicitacaoData = ref(null)

const abrirSolicitacaoAdiantamento = async () => {
  if (!item.value.idSolicitacaoAdiantamento) return
  isCarregandoSolicitacao.value = true
  try {
    const response = await api.get(`/SolicitacoesAdiantamento/${item.value.idSolicitacaoAdiantamento}`)
    solicitacaoData.value = response.data
    showModalSolicitacao.value = true
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar dados da solicitação.', type: 'error' })
  } finally {
    isCarregandoSolicitacao.value = false
  }
}

const isLoadingListas = ref(false)

const tipoFixoPorMenu = computed(() => {
  if (uiStore.currentModuleTitle?.includes('Pagar')) return FIN_TPTITULO.APagar
  if (uiStore.currentModuleTitle?.includes('Receber')) return FIN_TPTITULO.AReceber
  return ''
})

const tituloModulo = computed(() => {
  if (tipoFixoPorMenu.value === FIN_TPTITULO.APagar) return 'Título Financeiro a Pagar'
  if (tipoFixoPorMenu.value === FIN_TPTITULO.AReceber) return 'Título Financeiro a Receber'
  return 'Título Financeiro'
})

const accordionState = reactive({
  capa: true,
  cambio: false,
  itens: true,
  baixas: false
})

const listas = reactive({})

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const temPermissaoCompleta = computed(() =>
  authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
)
const isReadOnly = computed(() => isEditing.value && !temPermissaoCompleta.value)

const isBloqueadoPorNatureza = computed(() => {
  if (!isEditing.value) return false
  if (!item.value.lstNaturezaTitulo) return true
  return item.value.lstNaturezaTitulo !== FIN_NATUREZA_TITULO.LancamentosManuais
})

const isBloqueadoPorStatus = computed(() => {
  if (!isEditing.value) return false
  return item.value.lstStatusTitulo !== ST_TITULO_FIN.Aberto && item.value.lstStatusTitulo !== ST_TITULO_FIN.Rascunho
})

const mostrarBotaoDefinirTaxa = computed(() => {
  if (!isEditing.value) return false
  return true
})

const podeDefinirTaxaManual = computed(() => {
  if (!isEditing.value) return false
  const isNaturezaLiberada = item.value.lstNaturezaTitulo === FIN_NATUREZA_TITULO.LancamentosManuais
  const isStatusLiberado = item.value.lstStatusTitulo === ST_TITULO_FIN.Aberto || item.value.lstStatusTitulo === ST_TITULO_FIN.Rascunho
  return isNaturezaLiberada && isStatusLiberado
})

const podeDefinirTaxaAutomatica = computed(() => {
  if (!isEditing.value) return false
  const isNaturezaLiberada = item.value.lstNaturezaTitulo === FIN_NATUREZA_TITULO.LancamentosManuais
  const isStatusLiberado = item.value.lstStatusTitulo === ST_TITULO_FIN.Aberto || item.value.lstStatusTitulo === ST_TITULO_FIN.Rascunho
  return isNaturezaLiberada && isStatusLiberado
})

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const customEntityInfo = computed(() => {
  if (!isEditing.value) return entityInfo.value

  const partes = []

  // 1. Valor Total formatado
  if (item.value.valorTotal != null && item.value.valorTotal !== '') {
    partes.push(formatarNumeroView(item.value.valorTotal))
  }

  // 2. Nome do Parceiro
  if (item.value.nomeParceiro) {
    partes.push(item.value.nomeParceiro)
  }

  // 3. Documento / Fatura
  if (item.value.numeroFaturaOriginal) {
    partes.push(`Doc ${item.value.numeroFaturaOriginal}`)
  }

  // 4. Sigla da Moeda
  const siglaMoeda = obterSiglaMoeda(item.value.lstMoeda)
  if (siglaMoeda && siglaMoeda !== '-') {
    partes.push(siglaMoeda)
  }

  return {
    id: entityInfo.value.id,
    name: partes.join(' / '),
    nameLabel: 'Identificação'
  }
})

const configBuscaParceiro = computed(() => {
  const baseFiltros = { idEmpresa: authStore.empresaAtiva }

  if (item.value.lstTipoTitulo === FIN_TPTITULO.APagar) {
    baseFiltros.isFornecedor = true
  } else if (item.value.lstTipoTitulo === FIN_TPTITULO.AReceber) {
    baseFiltros.isCliente = true
  }

  return {
    ...parceiroSearch,
    titulo: 'Buscar Parceiro de Negócios',
    filtrosFixos: baseFiltros
  }
})

// Plano de categoria financeira vigente para filtro da busca
const idPlanoCategoriaFinanceira = ref(null)

/** Resolve o plano de categoria financeira ativo e vigente para a data atual */
const resolverPlanoCategoria = async () => {
  try {
    const res = await api.get('/PlanosCategoriasFinanceiras', {
      params: {
        lstStatusPlano: FIN_ST_PLAN_CATEGFIN.Ativo,
        idEmpresa: authStore.empresaAtiva,
        pageSize: 50
      }
    })
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const plano = (res.data || []).find(p => {
      if (!p.dataInicioVigencia || !p.dataFimVigencia) return false
      const inicio = new Date(p.dataInicioVigencia + 'T00:00:00')
      const fim = new Date(p.dataFimVigencia + 'T00:00:00')
      return hoje >= inicio && hoje <= fim
    })
    idPlanoCategoriaFinanceira.value = plano ? plano.idPlanoCategoriaFinanceira : null
    if (!idPlanoCategoriaFinanceira.value) {
      console.warn('[TitulosFinanceirosCadastro] Nenhum plano ativo vigente encontrado para a data atual.')
    }
  } catch (e) {
    console.warn('[TitulosFinanceirosCadastro] Erro ao resolver plano vigente:', e)
    idPlanoCategoriaFinanceira.value = null
  }
}

const configBuscaCategoria = ref(null)

const atualizarConfigBuscaCategoria = () => {
  if (!idPlanoCategoriaFinanceira.value) {
    configBuscaCategoria.value = null
    return
  }
  configBuscaCategoria.value = criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira',
    null,
    ST.Ativo,
    authStore.organizacaoAtiva,
    item.value.lstTipoTitulo || tipoFixoPorMenu.value,
    idPlanoCategoriaFinanceira.value
  )
}

watch([() => authStore.organizacaoAtiva, () => item.value.lstTipoTitulo, idPlanoCategoriaFinanceira], atualizarConfigBuscaCategoria, { immediate: true })

// --- COMPUTED DOS ITENS ---
const totalItensMoedaTitulo = computed(() => {
  if (!item.value.itens || !Array.isArray(item.value.itens)) return 0
  return item.value.itens.reduce((acc, current) => acc + (Number(current.valorMoedaTitulo) || 0), 0)
})

const totalItensMoedaPadrao = computed(() => {
  if (!item.value.itens || !Array.isArray(item.value.itens)) return 0
  return item.value.itens.reduce((acc, current) => acc + (Number(current.valorMoedaPadrao) || 0), 0)
})

const totalItensMoedaGerencial = computed(() => {
  if (!item.value.itens || !Array.isArray(item.value.itens)) return 0
  return item.value.itens.reduce((acc, current) => acc + (Number(current.valorMoedaGerencial) || 0), 0)
})

const percentualRateio = computed(() => {
  const total = Number(item.value.valorTotal) || 1
  return Math.min(100, Math.round((totalItensMoedaTitulo.value / total) * 100))
})

const classeBarraProgresso = computed(() => {
  if (percentualRateio.value >= 100) return 'bg-success'
  if (percentualRateio.value > 0) return 'bg-warning'
  return 'bg-secondary'
})

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['TituloFinanceiro'])
    Object.assign(listas, result)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados auxiliares.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

const obterNomeTipoMovimento = (codigo) => {
  if (!codigo) return '-'
  const itemLista = (listas.FIN_TP_MOVBANC || []).find(t => String(t.idListaSistemaItem) === String(codigo))
  return itemLista ? itemLista.textoLista : codigo
}

const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const idStr = String(codigoMoeda)
  const itemLista = (listas.LstMoeda || []).find(m =>
    String(m.idListaSistemaItem) === idStr ||
    String(m.idListaClienteItem) === idStr
  )
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

// --- LABELS DINÂMICOS PARA TAXAS DE CÂMBIO ---
const labelTaxaGerencialPadrao = computed(() => {
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaGerencial}/${siglaPadrao}`
})

const labelTaxaCambioGerencial = computed(() => {
  const siglaTitulo = obterSiglaMoeda(item.value.lstMoeda)
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  return `Taxa ${siglaTitulo}/${siglaGerencial}`
})

const labelTaxaPadrao = computed(() => {
  const siglaTitulo = obterSiglaMoeda(item.value.lstMoeda)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaTitulo}/${siglaPadrao}`
})

// --- DINÂMICA FORMA DE PAGAMENTO (NACIONAL vs INTERNACIONAL) ---
const isInternacional = computed(() => {
  if (!item.value.lstMoeda || !item.value.lstMoedaPadrao) return false
  return String(item.value.lstMoeda) !== String(item.value.lstMoedaPadrao)
})

// --- MODAL DE COTAÇÃO MANUAL ---
const isModalCotacaoManualOpen = ref(false)

const cotacaoManualData = reactive({
  taxaCambioPadrao: null,
  taxaCambioGerencialPadrao: null,
  taxaCambioGerencial: null
})

watch(
  () => [cotacaoManualData.taxaCambioPadrao, cotacaoManualData.taxaCambioGerencialPadrao],
  ([taxaPadrao, taxaGerencialPadrao]) => {
    if (taxaPadrao && taxaGerencialPadrao && Number(taxaGerencialPadrao) !== 0) {
      cotacaoManualData.taxaCambioGerencial = Math.round(
        (Number(taxaPadrao) / Number(taxaGerencialPadrao)) * 1e6
      ) / 1e6
    }
  }
)

const abrirModalCotacaoManual = () => {
  cotacaoManualData.taxaCambioPadrao = item.value.taxaCambioPadrao
  cotacaoManualData.taxaCambioGerencialPadrao = item.value.taxaCambioGerencialPadrao
  cotacaoManualData.taxaCambioGerencial = item.value.taxaCambioGerencial
  isModalCotacaoManualOpen.value = true
}

const salvarCotacaoManual = async () => {
  item.value.taxaCambioPadrao = cotacaoManualData.taxaCambioPadrao
  item.value.taxaCambioGerencialPadrao = cotacaoManualData.taxaCambioGerencialPadrao
  item.value.taxaCambioGerencial = cotacaoManualData.taxaCambioGerencial
  item.value.lstFonteCambio = MOE_FONTE_COTACAO.Manual
  item.value.lstTaxaCambioStatus = FIN_ST_TAX_CONVERCAO.Definitiva
  // NOTA: DataReferenciaCambio e DataAlvoBuscaCambio NÃO são manipulados aqui.
  // O ResolvedorTaxaCambioService é o único responsável por estes campos.
  // Quando lstFonteCambio = Manual, o resolvedor os limpará.

  const valorTotal = Number(item.value.valorTotal) || 0
  const taxaPadrao = Number(item.value.taxaCambioPadrao) || 0
  const taxaGerencial = Number(item.value.taxaCambioGerencial) || 0
  item.value.valorTotalMoedaPadrao = taxaPadrao > 0 ? Math.round(valorTotal * taxaPadrao * 100) / 100 : 0
  item.value.valorTotalMoedaGerencial = taxaGerencial > 0 ? Math.round(valorTotal * taxaGerencial * 100) / 100 : null

  if (item.value.idTituloFinanceiro) {
    try {
      const payload = {
        ...item.value,
        idParceiro: item.value.idParceiro ? Number(item.value.idParceiro) : null,
        idDocumentoEntrada: item.value.idDocumentoEntrada || null,
        idCondicaoPagamento: item.value.idCondicaoPagamento || null,
        idPurchaseOrderCondicaoPg: item.value.idPurchaseOrderCondicaoPg || null,
        idPedidoVendaCondicaoPg: item.value.idPedidoVendaCondicaoPg || null,
        saldoDevedor: item.value.saldoDevedor || item.value.valorTotal,
        itens: item.value.itens.map(i => ({
          idCategoriaFinanceira: i.idCategoriaFinanceira,
          valorMoedaTitulo: i.valorMoedaTitulo,
          valorMoedaPadrao: i.valorMoedaPadrao,
          valorMoedaGerencial: i.valorMoedaGerencial || null,
          descricaoComplementar: i.descricaoComplementar || null
        }))
      }
      await api.put(`/TitulosFinanceiros/${item.value.idTituloFinanceiro}`, payload)
      emit('show-notification', { message: 'Taxas de câmbio atualizadas com sucesso.', type: 'success' })
    } catch (error) {
      emit('show-notification', { message: 'Erro ao salvar taxas de câmbio.', type: 'error' })
    }
  }

  isModalCotacaoManualOpen.value = false
}

// --- RESOLUÇÃO AUTOMÁTICA DE TAXAS ---
const isResolvendoTaxas = ref(false)

const resolverTaxasAutomaticamente = async () => {
  if (!item.value.idTituloFinanceiro) {
    emit('show-notification', { message: 'Salve o título antes de resolver as taxas automaticamente.', type: 'warning' })
    return
  }

  isResolvendoTaxas.value = true
  try {
    const response = await api.post(`/TitulosFinanceiros/${item.value.idTituloFinanceiro}/resolver-taxas-automaticamente`)
    const dados = response.data

    item.value.lstMoedaPadrao = dados.lstMoedaPadrao
    item.value.taxaCambioPadrao = dados.taxaCambioPadrao
    item.value.lstTaxaCambioStatus = dados.lstTaxaCambioStatus
    item.value.dataReferenciaCambio = dados.dataReferenciaCambio
    item.value.dataAlvoBuscaCambio = dados.dataAlvoBuscaCambio
    item.value.lstFonteCambio = dados.lstFonteCambio
    item.value.lstMoedaGerencial = dados.lstMoedaGerencial
    item.value.taxaCambioGerencial = dados.taxaCambioGerencial
    item.value.taxaCambioGerencialPadrao = dados.taxaCambioGerencialPadrao
    item.value.valorTotalMoedaPadrao = dados.valorTotalMoedaPadrao
    item.value.valorTotalMoedaGerencial = dados.valorTotalMoedaGerencial

    emit('show-notification', { message: 'Taxas de câmbio resolvidas automaticamente com sucesso.', type: 'success' })
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao resolver taxas de câmbio automaticamente.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isResolvendoTaxas.value = false
  }
}

// --- GESTÃO DA TABELA FILHA (ITENS DO TÍTULO) ---
const isModalItemOpen = ref(false)

const novoItemModelo = {
  idCategoriaFinanceira: null,
  nomeCategoriaFinanceira: '',
  nomeExibicaoCategoriaFinanceira: '',
  codigoEstruturado: '',
  percentualRateio: null,
  valorMoedaTitulo: null,
  valorMoedaPadrao: 0,
  valorMoedaGerencial: null,
  descricaoComplementar: ''
}

const indiceItemEditando = ref(-1)
const modalItemParaEdicao = reactive({ ...novoItemModelo })

const _isSyncing = ref(false)

const limitePercentual = computed(() => {
  const total = Number(item.value.valorTotal) || 1

  if (indiceItemEditando.value === -1) {
    const pctUsado = (totalItensMoedaTitulo.value / total) * 100
    return Math.max(0, Math.min(100, +(100 - pctUsado).toFixed(2)))
  }

  const itemAtual = item.value.itens[indiceItemEditando.value]
  if (!itemAtual) return 100
  const valorOutros = totalItensMoedaTitulo.value - (Number(itemAtual.valorMoedaTitulo) || 0)
  const pctUsado = (valorOutros / total) * 100
  return Math.max(0, Math.min(100, +(100 - pctUsado).toFixed(2)))
})

const limiteValor = computed(() => {
  return +(Number(item.value.valorTotal) * limitePercentual.value / 100).toFixed(2)
})

const recalcularDoPercentual = () => {
  if (_isSyncing.value) return
  _isSyncing.value = true

  const valorTotal = Number(item.value.valorTotal) || 0
  const pct = Number(modalItemParaEdicao.percentualRateio) || 0
  const valor = +(valorTotal * (pct / 100)).toFixed(2) || 0

  modalItemParaEdicao.valorMoedaTitulo = valor

  const taxaPadrao = Number(item.value.taxaCambioPadrao) || 0
  const taxaGerencial = Number(item.value.taxaCambioGerencial) || 0

  modalItemParaEdicao.valorMoedaPadrao = taxaPadrao ? +(valor * taxaPadrao).toFixed(2) : valor
  modalItemParaEdicao.valorMoedaGerencial = taxaGerencial ? +(valor * taxaGerencial).toFixed(2) : null

  _isSyncing.value = false
}

const recalcularDoValor = () => {
  if (_isSyncing.value) return
  _isSyncing.value = true

  const valorTotal = Number(item.value.valorTotal) || 1
  const valor = Number(modalItemParaEdicao.valorMoedaTitulo) || 0
  const pct = Math.min(100, +(valor / valorTotal * 100).toFixed(2))

  modalItemParaEdicao.percentualRateio = pct

  const taxaPadrao = Number(item.value.taxaCambioPadrao) || 0
  const taxaGerencial = Number(item.value.taxaCambioGerencial) || 0

  modalItemParaEdicao.valorMoedaPadrao = taxaPadrao ? +(valor * taxaPadrao).toFixed(2) : valor
  modalItemParaEdicao.valorMoedaGerencial = taxaGerencial ? +(valor * taxaGerencial).toFixed(2) : null

  _isSyncing.value = false
}

const abrirModalItem = (index = -1) => {
  if (isReadOnly.value) return

  // Garante que a config de busca de categoria esteja atualizada antes de abrir o modal
  atualizarConfigBuscaCategoria()

  const total = Number(item.value.valorTotal) || 1

  if (index === -1) {
    indiceItemEditando.value = -1
    Object.assign(modalItemParaEdicao, novoItemModelo)
    const valorSugerido = Number(item.value.valorTotal) - totalItensMoedaTitulo.value
    if (valorSugerido > 0) {
      modalItemParaEdicao.percentualRateio = +(valorSugerido / total * 100).toFixed(2)
      recalcularDoPercentual()
    }
  } else {
    indiceItemEditando.value = index
    const itemExistente = item.value.itens[index]
    const pct = itemExistente.valorMoedaTitulo ? +(itemExistente.valorMoedaTitulo / total * 100).toFixed(2) : 0
    Object.assign(modalItemParaEdicao, itemExistente, { percentualRateio: pct })
  }

  isModalItemOpen.value = true
}

const salvarModalItem = () => {
  if (!modalItemParaEdicao.idCategoriaFinanceira) {
    emit('show-notification', { message: 'Categoria Financeira é obrigatória.', type: 'warning' })
    return
  }

  const temPercentual = modalItemParaEdicao.percentualRateio && modalItemParaEdicao.percentualRateio > 0
  const temValor = modalItemParaEdicao.valorMoedaTitulo && modalItemParaEdicao.valorMoedaTitulo > 0

  if (!temPercentual && !temValor) {
    emit('show-notification', { message: 'Informe o Percentual ou o Valor para rateio (maior que zero).', type: 'warning' })
    return
  }

  if (modalItemParaEdicao.percentualRateio > limitePercentual.value) {
    emit('show-notification', {
      message: `Percentual de ${modalItemParaEdicao.percentualRateio.toFixed(1)}% ultrapassa o limite disponível de ${limitePercentual.value.toFixed(1)}%. Ajuste o valor ou percentual.`,
      type: 'warning'
    })
    return
  }

  if (modalItemParaEdicao.valorMoedaTitulo > limiteValor.value) {
    emit('show-notification', {
      message: `Valor de ${formatarNumeroView(modalItemParaEdicao.valorMoedaTitulo)} ultrapassa o limite disponível de ${formatarNumeroView(limiteValor.value)}. Ajuste o valor ou percentual.`,
      type: 'warning'
    })
    return
  }

  recalcularDoPercentual()

  const payloadItem = { ...modalItemParaEdicao }
  delete payloadItem.percentualRateio

  if (indiceItemEditando.value === -1) {
    item.value.itens.push(payloadItem)
  } else {
    item.value.itens.splice(indiceItemEditando.value, 1, payloadItem)
  }

  isModalItemOpen.value = false
}

const removerItem = (index) => {
  item.value.itens.splice(index, 1)
}

const salvar = async () => {
  if (isReadOnly.value) return

  if (totalItensMoedaTitulo.value > 0 && Math.abs((Number(item.value.valorTotal) || 0) - totalItensMoedaTitulo.value) > 0.01) {
    console.warn('Atenção: A soma dos itens é diferente do Valor Total do documento.')
  }

  // Garantir o Tipo de Título a partir do menu módulo se não foi preenchido manualmente
  if (!item.value.lstTipoTitulo && tipoFixoPorMenu.value) {
    item.value.lstTipoTitulo = tipoFixoPorMenu.value
  }

  // NOTA: DataReferenciaCambio e DataAlvoBuscaCambio NÃO são enviados no payload.
  // O ResolvedorTaxaCambioService é o único responsável por determinar e persistir
  // estes campos. O frontend não deve defini-los.

  const payload = {
    ...item.value,
    idParceiro: item.value.idParceiro ? Number(item.value.idParceiro) : null,
    idDocumentoEntrada: item.value.idDocumentoEntrada || null,
    idCondicaoPagamento: item.value.idCondicaoPagamento || null,
    idPurchaseOrderCondicaoPg: item.value.idPurchaseOrderCondicaoPg || null,
    idPedidoVendaCondicaoPg: item.value.idPedidoVendaCondicaoPg || null,
    saldoDevedor: item.value.saldoDevedor || item.value.valorTotal,
    idSolicitacaoAdiantamento: item.value.idSolicitacaoAdiantamento || null,
    itens: item.value.itens.map(i => ({
      idCategoriaFinanceira: i.idCategoriaFinanceira,
      valorMoedaTitulo: i.valorMoedaTitulo,
      valorMoedaPadrao: i.valorMoedaPadrao,
      valorMoedaGerencial: i.valorMoedaGerencial || null,
      descricaoComplementar: i.descricaoComplementar || null
    }))
  }

  await submitForm('TitulosFinanceiros', 'idTituloFinanceiro', payload)
}

const formatarNumeroView = (valor, decimais = 2) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

const formatarTaxaView = (valor) => {
  return formatarNumeroView(valor, 4)
}

onMounted(() => {
  carregarListas()
  resolverPlanoCategoria()
  if (isEditing.value) {
    carregarHistoricoBaixas()
  }
})

watch(() => item.value.isAdiantamento, (newVal) => {
  if (newVal && !isReadOnly.value) {
    // A natureza para adiantamento será definida pelo backend
    // O watch apenas impede o usuário de alterar para outro valor enquanto adiantamento estiver ativo
  } else if (!newVal) {
    item.value.lstNaturezaTitulo = FIN_NATUREZA_TITULO.LancamentosManuais
  }
})

// --- HISTÓRICO DE BAIXAS ---
const historicoBaixas = ref([])
const isLoadingBaixas = ref(false)

const carregarHistoricoBaixas = async () => {
  if (!item.value.idTituloFinanceiro) return
  isLoadingBaixas.value = true
  try {
    const res = await api.get(`/MovimentacoesBancarias?idTituloFinanceiro=${item.value.idTituloFinanceiro}`)
    historicoBaixas.value = res.data || []
  } catch (error) {
    console.error('Erro ao carregar histórico de baixas', error)
  } finally {
    isLoadingBaixas.value = false
  }
}

// --- BAIXA / PAGAMENTO ---
const isModalBaixaOpen = ref(false)

const abrirModalBaixa = () => { isModalBaixaOpen.value = true }

const onBaixaSalva = (result) => {
  if (result) {
    // Atualiza o título com os dados retornados pelo backend
    item.value.lstStatusTitulo = result.lstStatusTitulo
    item.value.saldoDevedor = result.saldoDevedor
  }
  isModalBaixaOpen.value = false
  carregarHistoricoBaixas()
}
</script>

<template>
  <FormularioCadastro
    :module-name="tituloModulo"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="customEntityInfo"
    @submit="salvar"
    @cancelar="handleCancel"
    :desabilitar-salvar="isReadOnly"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- FRAME 1: CAPA DO DOCUMENTO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.capa = !accordionState.capa">
        <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i> Capa do Documento</h5>
        <i :class="['bi', accordionState.capa ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.capa">
        <div class="row g-3">

          <div class="col-md-3" v-if="!tipoFixoPorMenu">
            <CampoSelect id="lstTipoTitulo" label="Tipo de Título" v-model="item.lstTipoTitulo" :options="listas.LstTipoTitulo || []" option-label="textoLista" option-value="idListaSistemaItem" required :disabled="isReadOnly || isBloqueadoPorStatus" />
          </div>

          <div :class="item.idSolicitacaoAdiantamento ? 'col-md-2' : 'col-md-3'">
            <CampoSelect id="isAdiantamento" label="É Adiantamento?" v-model="item.isAdiantamento" :options="simNaoOptions" option-label="label" option-value="id" required :disabled="isReadOnly || isBloqueadoPorNatureza || isBloqueadoPorStatus" descricao-informativa="Se Sim, exigirá Prestação de Contas." />
          </div>
          <div class="col-md-1" v-if="item.idSolicitacaoAdiantamento">
            <label class="form-label d-block invisible">&nbsp;</label>
            <button
              type="button"
              class="btn btn-warning btn-sm py-1 px-2 w-100"
              :disabled="isCarregandoSolicitacao"
              @click="abrirSolicitacaoAdiantamento"
              title="Visualizar Solicitação de Adiantamento"
            >
              <i class="bi bi-rocket-takeoff-fill me-1"></i>
              <small>ADT #{{ item.idSolicitacaoAdiantamento }}</small>
            </button>
          </div>

          <div class="col-md-6">
            <CampoBuscaLista id="lstNaturezaTitulo" label="Natureza da Operação" v-model="item.lstNaturezaTitulo" tabela="TituloFinanceiro" required disabled @show-notification="p => emit('show-notification', p)" />
          </div>
<div class="col-md-6">
  <CampoBusca id="idParceiro" label="Parceiro (Credor / Devedor)" v-model="item.idParceiro" :configuracao-busca="configBuscaParceiro" required :disabled="isReadOnly || isBloqueadoPorNatureza || isBloqueadoPorStatus" @show-notification="p => emit('show-notification', p)" />
</div>
<div class="col-md-3">
  <CampoInput id="numeroFaturaOriginal" label="Doc / Fatura (Opcional)" v-model="item.numeroFaturaOriginal" placeholder="Ex: NF 12345" :disabled="isReadOnly || isBloqueadoPorNatureza || isBloqueadoPorStatus" />
</div>
<div class="col-md-2">
  <CampoInput id="dataEmissao" label="Data de Emissão" type="date" v-model="item.dataEmissao" required :disabled="isReadOnly || isBloqueadoPorNatureza || isBloqueadoPorStatus" />
</div>

          <div class="col-md-2">
            <CampoInput id="dataVencimento" label="Data de Vencimento" type="date" v-model="item.dataVencimento" required :disabled="isReadOnly || isBloqueadoPorStatus" />
          </div>

          <div class="col-md-2">
            <CampoInput id="numeroParcela" label="Parcela" v-model="item.numeroParcela" type="text" disabled />
          </div>
          <div class="col-md-2">
            <CampoInput id="totalParcelas" label="Total Parcelas" v-model="item.totalParcelas" type="text" disabled />
          </div>

          <div class="col-md-2">
            <CampoBuscaLista id="lstMoeda" label="Moeda do Documento" v-model="item.lstMoeda" tabela="TituloFinanceiro" required :disabled="isReadOnly || isBloqueadoPorStatus || isBloqueadoPorNatureza" />
          </div>

          <div class="col-md-2">
            <CampoInput id="valorTotal" label="Valor Total (Face)" type="moeda" v-model="item.valorTotal" required :disabled="isReadOnly || isBloqueadoPorNatureza || isBloqueadoPorStatus" />
          </div>

          <div class="col-md-2" v-if="isEditing">
            <div class="d-flex align-items-end gap-2">
              <div class="flex-grow-1">
                <CampoInput id="saldoDevedor" label="Saldo Devedor Atual" type="moeda" v-model="item.saldoDevedor" disabled />
              </div>
              <button
                type="button"
                class="btn btn-success mb-3"
                v-if="item.saldoDevedor > 0 && !isReadOnly && !isBloqueadoPorStatus"
                @click="abrirModalBaixa"
                title="Registrar Baixa / Pagamento"
              >
                <i class="bi bi-currency-dollar"></i> Baixar
              </button>
            </div>
          </div>

          <div class="col-md-12">
            <CampoInput id="observacaoLivre" label="Observações Livres" type="textarea" v-model="item.observacaoLivre" :readonly="isReadOnly || isBloqueadoPorStatus" />
          </div>

          <div class="col-md-3">
            <CampoSelect id="lstStatusTitulo" label="Status do Título" v-model="item.lstStatusTitulo" :options="listas.LstStatusTitulo || []" option-label="textoLista" option-value="idListaSistemaItem" :disabled="true" descricao-informativa="Gerido automaticamente pelo sistema via Baixas." />
          </div>

          <!-- Informações de Pagamento -->
          <div class="col-md-3" v-show="!isInternacional">
            <CampoBuscaLista id="lstFormaPagamento" label="Forma de Pagamento" v-model="item.lstFormaPagamento" tabela="TituloFinanceiro" :disabled="isReadOnly || isBloqueadoPorStatus" @show-notification="p => emit('show-notification', p)" />
          </div>
          <div class="col-md-3" v-show="isInternacional">
            <CampoBuscaLista id="lstFormaPagamentoInternacional" label="Forma de Pagamento (Internacional)" v-model="item.lstFormaPagamentoInternacional" tabela="TituloFinanceiro" :disabled="isReadOnly || isBloqueadoPorStatus" @show-notification="p => emit('show-notification', p)" />
          </div>
          <div class="col-md-3">
            <CampoInput id="codigoBarrasOuPix" label="Código Barras / Pix" v-model="item.codigoBarrasOuPix" placeholder="Linha digitável ou Pix copia-e-cola" :readonly="isReadOnly || isBloqueadoPorStatus" />
          </div>

        </div>
      </div>
    </div>

    <!-- FRAME 1.5: CÂMBIO E CONVERSÃO -->
    <div class="card mb-4" v-if="isEditing">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.cambio = !accordionState.cambio">
        <h5 class="mb-0"><i class="bi bi-currency-exchange me-2 text-primary"></i> Câmbio e Conversão</h5>
        <i :class="['bi', accordionState.cambio ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.cambio">

        <!-- Linha de cabeçalho do câmbio -->
        <div class="row g-3 mb-4 pb-3 border-bottom">
          <div class="col-md-3">
            <CampoBuscaLista id="lstTipoTaxaConversao" label="Tipo Taxa Conversão" v-model="item.lstTipoTaxaConversao" tabela="TituloFinanceiro" :disabled="true" descricao-informativa="Campos de conversão são gerados automaticamente" />
          </div>
          <div class="col-md-2">
            <CampoInput id="dataReferenciaCambio" label="Data Ref. Câmbio" type="date" v-model="item.dataReferenciaCambio" :disabled="true" descricao-informativa="Campos de conversão são gerados automaticamente" />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista id="lstFonteCambio" label="Fonte da Cotação" v-model="item.lstFonteCambio" tabela="TituloFinanceiro" :disabled="true" descricao-informativa="Campos de conversão são gerados automaticamente" />
          </div>
          <div class="col-md-4">
            <CampoBuscaLista id="lstTaxaCambioStatus" label="Status Taxa" v-model="item.lstTaxaCambioStatus" tabela="TituloFinanceiro" :disabled="true" descricao-informativa="Campos de conversão são gerados automaticamente" />
          </div>
        </div>

        <!-- Mini-cards: Moeda Padrão vs Moeda Gerencial -->
        <div class="row g-3">
          <div class="col-md-6">
            <div class="card border-primary h-100">
              <div class="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Padrão</h6>
                <div class="d-flex gap-1">
                  <button
                    v-if="mostrarBotaoDefinirTaxa && item.lstFonteCambio === MOE_FONTE_COTACAO.Manual"
                    type="button"
                    class="btn btn-sm btn-outline-light py-0"
                    :disabled="!podeDefinirTaxaAutomatica || isResolvendoTaxas"
                    @click.stop="resolverTaxasAutomaticamente"
                    title="Buscar cotações automaticamente"
                  >
                    <i class="bi bi-robot me-1"></i> Definir Automaticamente
                  </button>
                  <button
                    v-if="mostrarBotaoDefinirTaxa"
                    type="button"
                    class="btn btn-sm btn-light py-0"
                    :disabled="!podeDefinirTaxaManual"
                    @click.stop="abrirModalCotacaoManual"
                    title="Definir manualmente as taxas de câmbio"
                  >
                    <i class="bi bi-pencil-square me-1"></i> Definir Manualmente
                  </button>
                </div>
              </div>
              <div class="card-body py-2">
                <div class="row g-2">
                  <div class="col-4">
                    <CampoBuscaLista id="lstMoedaPadrao" label="Moeda" v-model="item.lstMoedaPadrao" tabela="TituloFinanceiro" :disabled="true" descricao-informativa="Campos de conversão são gerados automaticamente" />
                  </div>
                  <div class="col-4">
                    <CampoInput id="taxaCambioPadrao" label="Taxa Câmbio"
                      type="moeda" :casasDecimais="4"
                      :model-value="item.taxaCambioPadrao" disabled />
                  </div>
                  <div class="col-4">
                    <CampoInput id="valorTotalMoedaPadrao" label="Valor Total"
                      type="moeda"
                      :model-value="item.valorTotalMoedaPadrao" disabled />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card border-primary h-100">
              <div class="card-header bg-primary text-white py-2">
                <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Gerencial</h6>
              </div>
              <div class="card-body py-2">
                <div class="row g-2">
                  <div class="col-3">
                    <CampoBuscaLista id="lstMoedaGerencial" label="Moeda" v-model="item.lstMoedaGerencial" tabela="TituloFinanceiro" :disabled="true" descricao-informativa="Campos de conversão são gerados automaticamente" />
                  </div>
                  <div class="col-3">
                    <CampoInput id="taxaCambioGerencialPadrao"
                      :label="labelTaxaGerencialPadrao"
                      type="moeda" :casasDecimais="4"
                      :model-value="item.taxaCambioGerencialPadrao" disabled />
                  </div>
                  <div class="col-3">
                    <CampoInput id="taxaCambioGerencial"
                      :label="labelTaxaCambioGerencial"
                      type="moeda" :casasDecimais="4"
                      :model-value="item.taxaCambioGerencial" disabled />
                  </div>
                  <div class="col-3">
                    <CampoInput id="valorTotalMoedaGerencial" label="Valor Total"
                      type="moeda"
                      :model-value="item.valorTotalMoedaGerencial" disabled />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- FRAME 2: ITENS DO TÍTULO (Categorias Financeiras) -->
    <div class="card mb-4 border-primary">
      <div class="card-header bg-primary text-white cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.itens = !accordionState.itens">
        <h5 class="mb-0 text-white"><i class="bi bi-list-check me-2"></i> Categorias Financeiras</h5>
        <div class="d-flex align-items-center gap-3">
          <div class="progress" style="width:200px;height:20px;" title="Percentual rateado do Valor Total">
            <div class="progress-bar" :class="classeBarraProgresso" :style="{ width: percentualRateio + '%' }">
              {{ percentualRateio }}%
            </div>
          </div>
          <span class="badge bg-light text-primary fs-6">
            {{ formatarNumeroView(totalItensMoedaTitulo) }} / {{ formatarNumeroView(item.valorTotal) }}
          </span>
          <button type="button" class="btn btn-sm btn-light py-0" @click.stop="abrirModalItem(-1)" v-if="!isReadOnly && !isBloqueadoPorStatus">
            <i class="bi bi-plus-lg"></i>
          </button>
          <i :class="['bi', accordionState.itens ? 'bi-chevron-up text-white' : 'bi-chevron-down text-white']"></i>
        </div>
      </div>
      <div class="card-body border-top border-secondary-subtle p-0" v-show="accordionState.itens">
        <div class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th>Categoria Financeira</th>
                <th>Descrição</th>
                <th class="text-end">Valor {{ obterSiglaMoeda(item.lstMoeda) }} (Moeda Título)</th>
                <th class="text-end">Valor {{ obterSiglaMoeda(item.lstMoedaPadrao) }} (Moeda Padrão)</th>
                <th class="text-end">Valor {{ obterSiglaMoeda(item.lstMoedaGerencial) }} (Moeda Gerencial)</th>
                <th class="text-end" style="width:80px;" v-if="!isReadOnly && !isBloqueadoPorStatus">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(linha, index) in item.itens" :key="linha.idTituloFinanceiroItem || index">
                <td class="fw-medium">{{ linha.nomeExibicaoCategoriaFinanceira || linha.nomeCategoriaFinanceira || '-' }}</td>
                <td><small>{{ linha.descricaoComplementar || '-' }}</small></td>
                <td class="text-end fw-bold">{{ formatarNumeroView(linha.valorMoedaTitulo) }}</td>
                <td class="text-end">{{ formatarNumeroView(linha.valorMoedaPadrao) }}</td>
                <td class="text-end">{{ linha.valorMoedaGerencial ? formatarNumeroView(linha.valorMoedaGerencial) : '-' }}</td>
                <td class="text-end" v-if="!isReadOnly && !isBloqueadoPorStatus">
                  <button type="button" class="btn-icon text-primary" @click="abrirModalItem(index)"><i class="bi bi-pencil-fill"></i></button>
                  <button type="button" class="btn-icon text-danger" @click="removerItem(index)"><i class="bi bi-trash-fill"></i></button>
                </td>
              </tr>
              <tr v-if="item.itens.length === 0">
                <td :colspan="isReadOnly || isBloqueadoPorStatus ? 5 : 6" class="text-center text-muted py-4">
                  Nenhum rateio definido. O título está sem detalhamento contábil.
                </td>
              </tr>
            </tbody>
            <tfoot v-if="item.itens.length > 0">
              <tr class="table-active fw-bold">
                <td colspan="2" class="text-end">TOTAIS:</td>
                <td class="text-end">{{ formatarNumeroView(totalItensMoedaTitulo) }}</td>
                <td class="text-end">{{ formatarNumeroView(totalItensMoedaPadrao) }}</td>
                <td class="text-end">{{ totalItensMoedaGerencial ? formatarNumeroView(totalItensMoedaGerencial) : '-' }}</td>
                <td v-if="!isReadOnly && !isBloqueadoPorStatus"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- FRAME 3: HISTÓRICO DE BAIXAS -->
    <div class="card mb-4" v-if="isEditing">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.baixas = !accordionState.baixas">
        <h5 class="mb-0"><i class="bi bi-bank2 me-2 text-primary"></i> Histórico de Baixas / Pagamentos</h5>
        <div>
          <span class="badge bg-secondary text-white fs-6 me-3" v-if="historicoBaixas.length > 0">{{ historicoBaixas.length }} Registo(s)</span>
          <i :class="['bi', accordionState.baixas ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
      </div>
      <div class="card-body p-0" v-show="accordionState.baixas">
        <div v-if="isLoadingBaixas" class="text-center p-4">
          <div class="spinner-border text-primary spinner-border-sm me-2"></div> Carregando...
        </div>
        <div v-else class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th>Data Pagamento</th>
                <th>Tipo Movimento</th>
                <th>Conta Bancária</th>
                <th>Observação</th>
                <th class="text-end">Valor Amortizado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="baixa in historicoBaixas" :key="baixa.idMovimentacaoBancaria">
                <td class="fw-medium">{{ baixa.dataPagamento ? formatarDataBR(baixa.dataPagamento) : '-' }}</td>
                <td>
                  <span class="badge bg-body-tertiary text-body border border-secondary-subtle">
                    {{ obterNomeTipoMovimento(baixa.lstTipoMovimentacao) }}
                  </span>
                </td>
                <td class="text-body-secondary">{{ baixa.nomeContaBancaria || 'Baixa Virtual / Conciliação' }}</td>
                <td class="text-truncate" style="max-width: 250px;" :title="baixa.observacao">{{ baixa.observacao || '-' }}</td>
                <td class="text-end fw-bold text-success">{{ formatarNumeroView(baixa.valorAmortizado) }}</td>
              </tr>
              <tr v-if="historicoBaixas.length === 0">
                <td colspan="5" class="text-center text-muted py-4">Nenhum pagamento ou baixa registado para este título.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </FormularioCadastro>

  <!-- MODAL PARA ADICIONAR/EDITAR ITEM DO TÍTULO (Categoria Financeira) -->
  <ModalFormulario
    v-model="isModalItemOpen"
    :titulo="indiceItemEditando === -1 ? 'Adicionar Categoria Financeira' : 'Editar Categoria Financeira'"
    largura="modal-lg"
    textoConfirmar="Salvar Categoria Financeira"
    @confirmar="salvarModalItem"
  >
    <div class="row g-3">
      <div class="col-md-12">
        <CampoBusca id="idCategoriaFinanceira" label="Categoria Financeira (DRE)"
          v-model="modalItemParaEdicao.idCategoriaFinanceira"
          :configuracao-busca="configBuscaCategoria"
          required
          @selecionado="val => { modalItemParaEdicao.nomeCategoriaFinanceira = val.nome; modalItemParaEdicao.nomeExibicaoCategoriaFinanceira = val.nomeExibicao; modalItemParaEdicao.codigoEstruturado = val.codigoEstruturado }"
          @show-notification="p => emit('show-notification', p)" />
      </div>
      <div class="col-md-5">
        <div class="border rounded p-3">
          <div class="row g-3">
            <div class="col-md-6">
              <CampoInput id="modalPercentualRateio" label="Percentual (%)"
                type="percentual" dynamic-decimals v-model="modalItemParaEdicao.percentualRateio"
                required @input="recalcularDoPercentual"
              />
            </div>
            <div class="col-md-6">
              <CampoInput id="modalValorRateio" :label="`Valor (${obterSiglaMoeda(item.lstMoeda)})`"
                type="moeda" v-model="modalItemParaEdicao.valorMoedaTitulo"
                required @input="recalcularDoValor" />
            </div>
            <div class="d-flex align-items-center gap-2 mt-1">
              <button type="button" class="btn btn-outline-secondary btn-sm" style="font-size:0.75rem;line-height:1;width:38px;"
                :disabled="(modalItemParaEdicao.percentualRateio ?? 0) <= 0"
                @click="modalItemParaEdicao.percentualRateio = Math.max(0, (modalItemParaEdicao.percentualRateio ?? 0) - 0.1); recalcularDoPercentual()">
                -0,1
              </button>
              <input type="range" class="form-range" min="0" step="0.1"
                :max="limitePercentual"
                :value="modalItemParaEdicao.percentualRateio ?? 0"
                @input="modalItemParaEdicao.percentualRateio = Math.min(limitePercentual, Math.max(0, parseFloat($event.target.value) || 0)); recalcularDoPercentual()" />
              <button type="button" class="btn btn-outline-secondary btn-sm" style="font-size:0.75rem;line-height:1;width:38px;"
                :disabled="(modalItemParaEdicao.percentualRateio ?? 0) >= limitePercentual"
                @click="modalItemParaEdicao.percentualRateio = Math.min(limitePercentual, (modalItemParaEdicao.percentualRateio ?? 0) + 0.1); recalcularDoPercentual()">
                +0,1
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-7">
        <CampoInput id="descricaoComplementar" label="Descrição Complementar"
          type="text" v-model="modalItemParaEdicao.descricaoComplementar"
          placeholder="Ex: Referente ao Lote XYZ..." />
      </div>
    </div>

    <!-- Sub-frame: Valores do Título -->
    <div class="card mt-3 border-secondary">
      <div class="card-header bg-body-tertiary py-2">
        <h6 class="mb-0"><i class="bi bi-currency-dollar me-1 text-primary small"></i> Valores do Título</h6>
      </div>
      <div class="card-body py-2 px-0">
        <table class="table table-sm table-borderless mb-0">
          <thead class="bg-body-tertiary">
            <tr>
              <th class="ps-3" style="width:120px;">&nbsp;</th>
              <th class="text-center">Moeda Título<br><small class="text-muted fw-normal">{{ obterSiglaMoeda(item.lstMoeda) }}</small></th>
              <th class="text-center">Moeda Padrão<br><small class="text-muted fw-normal">{{ obterSiglaMoeda(item.lstMoedaPadrao) }}</small></th>
              <th class="text-center">Moeda Gerencial<br><small class="text-muted fw-normal">{{ obterSiglaMoeda(item.lstMoedaGerencial) }}</small></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="ps-3 text-secondary">Taxa Câmbio</td>
              <td class="text-center text-muted">-</td>
              <td class="text-center">{{ item.taxaCambioPadrao ? formatarTaxaView(item.taxaCambioPadrao) : '-' }}</td>
              <td class="text-center">{{ item.taxaCambioGerencial ? formatarTaxaView(item.taxaCambioGerencial) : '-' }}</td>
            </tr>
            <tr>
              <td class="ps-3 text-secondary">Valor Total</td>
              <td class="text-center fw-bold">{{ formatarNumeroView(item.valorTotal) }}</td>
              <td class="text-center">{{ item.valorTotalMoedaPadrao ? formatarNumeroView(item.valorTotalMoedaPadrao) : '-' }}</td>
              <td class="text-center">{{ item.valorTotalMoedaGerencial ? formatarNumeroView(item.valorTotalMoedaGerencial) : '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </ModalFormulario>

  <!-- MODAL DE BAIXA FINANCEIRA (Seção 11.1) -->
  <BaixaTituloFinanceiro
    :is-open="isModalBaixaOpen"
    :titulo-financeiro="item"
    @close="isModalBaixaOpen = false"
    @salvo="onBaixaSalva"
    @show-notification="(p) => emit('show-notification', p)"
  />

  <!-- MODAL DE COTAÇÃO MANUAL -->
  <ModalFormulario
    v-model="isModalCotacaoManualOpen"
    titulo="Definir Taxas de Câmbio Manualmente"
    tamanho="xs"
    textoConfirmar="Salvar Taxas"
    @confirmar="salvarCotacaoManual"
  >
    <div class="d-flex flex-column gap-2" >
      <CampoInput id="modalTaxaCambioPadrao"
        :label="labelTaxaPadrao"
        type="moeda" :casasDecimais="4"
        v-model="cotacaoManualData.taxaCambioPadrao" />
      <CampoInput id="modalTaxaCambioGerencialPadrao"
        :label="labelTaxaGerencialPadrao"
        type="moeda" :casasDecimais="4"
        v-model="cotacaoManualData.taxaCambioGerencialPadrao" />
      <CampoInput id="modalTaxaCambioGerencial"
        :label="labelTaxaCambioGerencial"
        type="moeda" :casasDecimais="4"
        :model-value="cotacaoManualData.taxaCambioGerencial" disabled />
    </div>
    <div class="mt-2 text-muted small">
      <i class="bi bi-info-circle me-1"></i>
      O campo "{{ labelTaxaCambioGerencial }}" é calculado automaticamente:
      {{ labelTaxaPadrao }} &divide; {{ labelTaxaGerencialPadrao }}
    </div>
  </ModalFormulario>

  <!-- Componente de Visualização do Histórico -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="TituloFinanceiro"
    :chave-primaria="item.idTituloFinanceiro"
    :titulo-registro="`Título ${item.idTituloFinanceiro}`"
    contexto="tenant"
    :dicionarios="{ 'lstTipoTitulo': listas.LstTipoTitulo, 'lstStatusTitulo': listas.LstStatusTitulo }"
    @fechar="mostrarAuditoria = false"
  />

  <!-- Modal de Visualização da Solicitação de Adiantamento -->
  <SolicitacaoAdiantamentoModal
    v-model="showModalSolicitacao"
    modo="visualizacao"
    :solicitacao="solicitacaoData"
    :id-organizacao="authStore.empresaAtiva"
    @show-notification="(p) => emit('show-notification', p)"
  />
</template>
